/* ═══════════════════════════════════════════════════════
   src/content/domains/cards/blade.tsx
   All Blade domain cards with text pre-rendered as JSX.
   Edit the `rendered` field to update display content.
   Edit the `text` field to update search index content.
   ═══════════════════════════════════════════════════════ */

import { DomainCard } from './types';
import {
  STRESS,
  HOPE,
  VERY_CLOSE,
  MELEE,
  LONG_REST,
  HIT_POINT,
  ARMOR_SLOT,
  WEAPON_DAMAGE,
  ADVANTAGE,
  FRENZY,
  FRENZIED,
  LOADOUT,
  VAULT,
  REACTION_ROLL,
} from '../../templates';

const bladeCards: DomainCard[] = [
  {
    name: "Get Back Up",
    level: 1,
    domain: "Blade",
    type: "Ability",
    recallCost: 1,
    text: "When you take Severe damage, you can **mark a Stress** to reduce the severity by one threshold.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you take Severe damage, you can <strong className="inline-strong">mark a {STRESS}</strong> to reduce the severity by one threshold.</p>
      </>
    ),
  },
  {
    name: "Not Good Enough",
    level: 1,
    domain: "Blade",
    type: "Ability",
    recallCost: 1,
    text: "When you roll your damage dice, you can reroll any 1s or 2s.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you roll your damage dice, you can reroll any 1s or 2s.</p>
      </>
    ),
  },
  {
    name: "Whirlwind",
    level: 1,
    domain: "Blade",
    type: "Ability",
    recallCost: 0,
    text: "When you make a successful attack against a target within Very Close range, you can **spend a Hope** to use the attack against all other targets within Very Close range. All additional adversaries you succeed against with this ability take half damage.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you make a successful attack against a target within {VERY_CLOSE} range, you can <strong className="inline-strong">spend a {HOPE}</strong> to use the attack against all other targets within {VERY_CLOSE} range. All additional adversaries you succeed against with this ability take half damage.</p>
      </>
    ),
  },
  {
    name: "A Soldier's Bond",
    level: 2,
    domain: "Blade",
    type: "Ability",
    recallCost: 1,
    text: "Once per long rest, when you compliment someone or ask them about something they're good at, you can both gain 3 Hope.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per {LONG_REST}, when you compliment someone or ask them about something they're good at, you can both gain 3 {HOPE}.</p>
      </>
    ),
  },
  {
    name: "Reckless",
    level: 2,
    domain: "Blade",
    type: "Ability",
    recallCost: 1,
    text: "**Mark a Stress** to gain advantage on an attack.",
    rendered: (
      <>
        <p key={0} className="card-text-para"><strong className="inline-strong">Mark a {STRESS}</strong> to gain {ADVANTAGE} on an attack.</p>
      </>
    ),
  },
  {
    name: "Scramble",
    level: 3,
    domain: "Blade",
    type: "Ability",
    recallCost: 1,
    text: "Once per rest, when a creature within Melee range would deal damage to you, you can avoid the attack and safely move out of Melee range of the enemy.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per rest, when a creature within {MELEE} range would deal damage to you, you can avoid the attack and safely move out of {MELEE} range of the enemy.</p>
      </>
    ),
  },
  {
    name: "Versatile Fighter",
    level: 3,
    domain: "Blade",
    type: "Ability",
    recallCost: 1,
    text: "You can use a different character trait for an equipped weapon, rather than the trait the weapon calls for.\n\nWhen you deal damage, you can **mark a Stress** to use the maximum result of one of your damage dice instead of rolling it.",
    rendered: (
      <>
        <p key={0} className="card-text-para">You can use a different character trait for an equipped weapon, rather than the trait the weapon calls for.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">When you deal damage, you can <strong className="inline-strong">mark a {STRESS}</strong> to use the maximum result of one of your damage dice instead of rolling it.</p>
      </>
    ),
  },
  {
    name: "Deadly Focus",
    level: 4,
    domain: "Blade",
    type: "Ability",
    recallCost: 2,
    text: "Once per rest, you can apply all your focus toward a target of your choice. Until you attack another creature, you defeat the target, or the battle ends, gain a +1 bonus to your Proficiency.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per rest, you can apply all your focus toward a target of your choice. Until you attack another creature, you defeat the target, or the battle ends, gain a +1 bonus to your Proficiency.</p>
      </>
    ),
  },
  {
    name: "Fortified Armor",
    level: 4,
    domain: "Blade",
    type: "Ability",
    recallCost: 0,
    text: "While you are wearing armor, gain a +2 bonus to your damage thresholds.",
    rendered: (
      <>
        <p key={0} className="card-text-para">While you are wearing armor, gain a +2 bonus to your damage thresholds.</p>
      </>
    ),
  },
  {
    name: "Champion's Edge",
    level: 5,
    domain: "Blade",
    type: "Ability",
    recallCost: 1,
    text: "When you critically succeed on an attack, you can **spend up to 3 Hope** and choose one of the following options for each Hope spent:\n\n- You clear a Hit Point.\n- You clear an Armor Slot.\n- The target must mark an additional Hit Point.\n\nYou can't choose the same option more than once.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you critically succeed on an attack, you can <strong className="inline-strong">spend up to 3 {HOPE}</strong> and choose one of the following options for each {HOPE} spent:</p>
        <div key={1} className="card-text-gap" />
        <div key={2} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>You clear a {HIT_POINT}.</span></div>
        <div key={3} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>You clear an {ARMOR_SLOT}.</span></div>
        <div key={4} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>The target must mark an additional {HIT_POINT}.</span></div>
        <div key={5} className="card-text-gap" />
        <p key={6} className="card-text-para">You can't choose the same option more than once.</p>
      </>
    ),
  },
  {
    name: "Vitality",
    level: 5,
    domain: "Blade",
    type: "Ability",
    recallCost: 0,
    text: "When you choose this card, permanently gain two of the following benefits:\n\n- One Stress slot\n- One Hit Point slot\n- +2 bonus to your damage thresholds\n\nThen place this card in your vault permanently.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you choose this card, permanently gain two of the following benefits:</p>
        <div key={1} className="card-text-gap" />
        <div key={2} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>One {STRESS} slot</span></div>
        <div key={3} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>One {HIT_POINT} slot</span></div>
        <div key={4} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>+2 bonus to your damage thresholds</span></div>
        <div key={5} className="card-text-gap" />
        <p key={6} className="card-text-para">Then place this card in your {VAULT} permanently.</p>
      </>
    ),
  },
  {
    name: "Battle-Hardened",
    level: 6,
    domain: "Blade",
    type: "Ability",
    recallCost: 2,
    text: "Once per long rest when you would make a Death Move, you can **spend a Hope** to clear a Hit Point instead.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per {LONG_REST} when you would make a Death Move, you can <strong className="inline-strong">spend a {HOPE}</strong> to clear a {HIT_POINT} instead.</p>
      </>
    ),
  },
  {
    name: "Rage Up",
    level: 6,
    domain: "Blade",
    type: "Ability",
    recallCost: 1,
    text: "Before you make an attack, you can **mark a Stress** to gain a bonus to your damage roll equal to twice your Strength.\n\nYou can Rage Up twice per attack.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Before you make an attack, you can <strong className="inline-strong">mark a {STRESS}</strong> to gain a bonus to your damage roll equal to twice your Strength.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">You can Rage Up twice per attack.</p>
      </>
    ),
  },
  {
    name: "Blade-Touched",
    level: 7,
    domain: "Blade",
    type: "Ability",
    recallCost: 1,
    text: "When 4 or more of the domain cards in your loadout are from the Blade domain, gain the following benefits:\n\n- +2 bonus to your attack rolls\n- +4 bonus to your Severe damage threshold",
    rendered: (
      <>
        <p key={0} className="card-text-para">When 4 or more of the domain cards in your {LOADOUT} are from the Blade domain, gain the following benefits:</p>
        <div key={1} className="card-text-gap" />
        <div key={2} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>+2 bonus to your attack rolls</span></div>
        <div key={3} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>+4 bonus to your Severe damage threshold</span></div>
      </>
    ),
  },
  {
    name: "Glancing Blow",
    level: 7,
    domain: "Blade",
    type: "Ability",
    recallCost: 1,
    text: "When you fail an attack, you can **mark a Stress** to deal weapon damage using half your Proficiency.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you fail an attack, you can <strong className="inline-strong">mark a {STRESS}</strong> to deal {WEAPON_DAMAGE} using half your Proficiency.</p>
      </>
    ),
  },
  {
    name: "Battle Cry",
    level: 8,
    domain: "Blade",
    type: "Ability",
    recallCost: 2,
    text: "Once per long rest, while you're charging into danger, you can muster a rousing call that inspires your allies. All allies who can hear you each clear a Stress and gain a Hope. Additionally, your allies gain advantage on attack rolls until you or an ally rolls a failure with Fear.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per {LONG_REST}, while you're charging into danger, you can muster a rousing call that inspires your allies. All allies who can hear you each clear a {STRESS} and gain a {HOPE}. Additionally, your allies gain {ADVANTAGE} on attack rolls until you or an ally rolls a failure with Fear.</p>
      </>
    ),
  },
  {
    name: "Frenzy",
    level: 8,
    domain: "Blade",
    type: "Ability",
    recallCost: 3,
    text: "Once per long rest, you can go into a _Frenzy_ until there are no more adversaries within sight.\n\nWhile _Frenzied_, you can't use Armor Slots, and you gain a +10 bonus to your damage rolls and a +8 bonus to your Severe damage threshold.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per {LONG_REST}, you can go into a <em>{FRENZY}</em> until there are no more adversaries within sight.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">While <em>{FRENZIED}</em>, you can't use {ARMOR_SLOT}s, and you gain a +10 bonus to your damage rolls and a +8 bonus to your Severe damage threshold.</p>
      </>
    ),
  },
  {
    name: "Gore and Glory",
    level: 9,
    domain: "Blade",
    type: "Ability",
    recallCost: 2,
    text: "When you critically succeed on a weapon attack, gain an additional Hope or clear an additional Stress.\n\nAdditionally, when you deal enough damage to defeat an enemy, gain a Hope or clear a Stress.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you critically succeed on a weapon attack, gain an additional {HOPE} or clear an additional {STRESS}.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">Additionally, when you deal enough damage to defeat an enemy, gain a {HOPE} or clear a {STRESS}.</p>
      </>
    ),
  },
  {
    name: "Reaper's Strike",
    level: 9,
    domain: "Blade",
    type: "Ability",
    recallCost: 3,
    text: "Once per long rest, **spend a Hope** to make an attack roll. The GM tells you which targets within range it would succeed against. Choose one of these targets and force them to mark 5 Hit Points.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per {LONG_REST}, <strong className="inline-strong">spend a {HOPE}</strong> to make an attack roll. The GM tells you which targets within range it would succeed against. Choose one of these targets and force them to mark 5 {HIT_POINT}s.</p>
      </>
    ),
  },
  {
    name: "Battle Monster",
    level: 10,
    domain: "Blade",
    type: "Ability",
    recallCost: 0,
    text: "When you make a successful attack against an adversary, you can **mark 4 Stress** to force the target to mark a number of Hit Points equal to the number of Hit Points you currently have marked instead of rolling for damage.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you make a successful attack against an adversary, you can <strong className="inline-strong">mark 4 {STRESS}</strong> to force the target to mark a number of {HIT_POINT}s equal to the number of {HIT_POINT}s you currently have marked instead of rolling for damage.</p>
      </>
    ),
  },
  {
    name: "Onslaught",
    level: 10,
    domain: "Blade",
    type: "Ability",
    recallCost: 3,
    text: "When you successfully make an attack with your weapon, you never deal damage beneath a target's Major damage threshold (the target always marks a minimum of 2 Hit Points).\n\nAdditionally, when a creature within your weapon's range deals damage to an ally with an attack that doesn't include you, you can **mark a Stress** to force them to make a Reaction Roll (15).\n\nOn a failure, the target must mark a Hit Point.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you successfully make an attack with your weapon, you never deal damage beneath a target's Major damage threshold (the target always marks a minimum of 2 {HIT_POINT}s).</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">Additionally, when a creature within your weapon's range deals damage to an ally with an attack that doesn't include you, you can <strong className="inline-strong">mark a {STRESS}</strong> to force them to make a {REACTION_ROLL} (15).</p>
        <div key={3} className="card-text-gap" />
        <p key={4} className="card-text-para">On a failure, the target must mark a {HIT_POINT}.</p>
      </>
    ),
  },
];

export default bladeCards;
